import './ArrivalBookingOnDevice.scss';
import {useTranslation} from 'react-i18next';
import {EidAuthenticate} from '../../authentication/EidAuthenticate';
import {useEffect, useState} from 'react';
import {Button} from '../../form/button/Button';
import {Input} from '../../form/input/Input';
import {fetchPerson, performBookingArrival, postValidatePnr} from '../../../services/api/api';
import {useArrivalContext} from '../../../context/ArrivalContext';
import {ArrivalConfirmation} from '../confirmation/ArrivalConfirmation';
import {ArrivalNotFound} from '../confirmation/ArrivalNotFound';
import {EIdPollData} from '../../../services/api/implementations/eidapi';
import {AuthenticatedPeopleList} from '../../clinic/booking/people-select/components/AuthenticatedPeople';
import {User} from 'src/context/BookingContext';
import {isOnlyMyself} from 'src/services/user/profile';
import {getSystemCountry} from 'src/services/i18n/locale';
import {getIdentityImpl} from 'src/services/i18n/identity';

type ArrivalStatus = 'confirmed' | 'not_found' | 'selecting_id_type' | 'input_pnr' | 'select_people';

interface ArrivalBookingOnDeviceProps {
    setShowNav: (show: boolean) => void;
}

const systemCountry = getSystemCountry();

const identityImpl = getIdentityImpl();

async function validateIdentify(identifier: string): Promise<boolean> {
    if (!identityImpl.isNumberComplete(identifier)) return false;
    try {
        await postValidatePnr(identifier); // ignore result, just non-error is okay
        return true;
    } catch {
        return false;
    }
}

export function ArrivalBookingOnDevice({setShowNav}: ArrivalBookingOnDeviceProps) {
    const {dataState} = useArrivalContext();
    const {t} = useTranslation();
    const [identity, setIdentity] = useState('');
    const [validId, setValidId] = useState(false);
    const [status, setStatus] = useState<ArrivalStatus>('selecting_id_type');
    const {settings, loading} = dataState;
    const [user, setUser] = useState<User>();

    useEffect(() => {
        validateIdentify(identity).then(setValidId);
    }, [identity]);

    function updateIdentity(id: string) {
        if (id.length > 12) return;
        setIdentity(id);
    }

    async function submitBookingArrival(id: string) {
        try {
            await performBookingArrival(dataState.clinicId, id, null);
            setStatus('confirmed');
        } catch {
            setStatus('not_found');
        }
    }

    function selectManualId() {
        setStatus('input_pnr');
        setShowNav(false);
    }

    async function loginSuccess(pollData: EIdPollData) {
        setStatus('select_people');
        setShowNav(false);

        const userJSON = await fetchPerson(pollData.jwt);
        setUser(userJSON as User);
        if (isOnlyMyself(userJSON)) {
            setIdentity(userJSON.identity);
        } else {
            setIdentity('');
        }
    }

    function selectPerson(id: string) {
        if (id === identity) setIdentity('');
        else setIdentity(id);
    }

    const selectedPersonsIds = !identityImpl.isNumberComplete(identity) ? [] : [identity];

    if (loading) return <span className='skeleton-box'></span>;

    const inputMode = systemCountry === 'sv' ? 'numeric' : 'text';

    return (
        <div>
            {status === 'selecting_id_type' && (
                <>
                    <h2 className='handle-booking__identify__heading'>{t('people_select.choose_method')}</h2>
                    <div className='handle-booking__identify-booking-code'>
                        <div className='login__section__buttons'>
                            <button className={'login__section__buttons__button'} onClick={selectManualId}>
                                <span>{t(`arrival.identifier_label.${systemCountry}`)}</span>
                            </button>
                        </div>
                    </div>
                    {settings?.bookingEid && <EidAuthenticate onLoginSuccess={loginSuccess} />}
                </>
            )}

            {status === 'input_pnr' && (
                <>
                    <h1 className='main-content__header'>{t('arrival.heading')}</h1>
                    <div className='booking-code-form__fields'>
                        <Input
                            id='pnr'
                            type='text'
                            inputMode={inputMode}
                            value={identity}
                            isValid={validId || identity.length === 0}
                            onChange={(val) => updateIdentity(val)}
                            label={t(`arrival.identifier_label.${systemCountry}`)}
                            ariaLabel={t(`arrival.identifier_label.${systemCountry}`)}
                            placeholder={t(`arrival.identifier_placeholder.${systemCountry}`)}
                            required
                        />
                        {!validId && identity.length > 0 && (
                            <p className='input-error'>{t(`arrival.identifier_validation_error.${systemCountry}`)}</p>
                        )}
                    </div>
                    <div className='booking-code-modal__footer' style={{textAlign: 'right'}}>
                        <Button disabled={!validId} onClick={() => submitBookingArrival(identity)}>
                            <span>{t('button_next.text')}</span>
                        </Button>
                    </div>
                </>
            )}

            {status === 'select_people' && (
                <>
                    <h1 className='main-content__header'>{t('arrival.heading')}</h1>
                    <AuthenticatedPeopleList
                        loading={!user}
                        user={user ?? null}
                        selfBookingOnly={settings?.eidSelfOnly === true}
                        selectedPersonsIds={selectedPersonsIds}
                        selectPerson={selectPerson}
                    />
                    <div style={{textAlign: 'right', marginTop: '1rem'}}>
                        <Button
                            disabled={selectedPersonsIds.length === 0}
                            onClick={() => submitBookingArrival(identity)}
                        >
                            <span>{t('button_next.text')}</span>
                        </Button>
                    </div>
                </>
            )}

            {status === 'confirmed' && <ArrivalConfirmation />}
            {status === 'not_found' && <ArrivalNotFound />}
        </div>
    );
}
